/**
 * Mock Slack Web Client
 * Implements ISlackClient interface against local mock storage.
 */

import { ApiEnvelope, SlackThread, SlackMessage } from '../../domain/types';
import { ITEMS } from '../../data/initialData';
import { ISlackClient, ClientConfig, SlackConnectionAck } from './types';
import {
  simulateNetwork,
  DEFAULT_CONFIG,
  isMockSourceDisconnected,
  isMockSourceRejected,
  mockRejectedCredentialMessage,
} from './mockClientUtils';

function buildMockSlackConnectionAck(): SlackConnectionAck {
  const checkedAt = new Date().toISOString();
  if (isMockSourceRejected('slack')) {
    return {
      status: 'error',
      teamName: 'Zunelo Mock Workspace',
      userName: 'currentUser',
      userId: 'U_CURRENT',
      checkedAt,
      error: mockRejectedCredentialMessage('slack'),
      code: 'SLACK_TOKEN_INVALID',
    };
  }
  if (isMockSourceDisconnected('slack')) {
    return {
      status: 'not_configured',
      teamName: null,
      userName: null,
      userId: null,
      checkedAt,
      error: 'Slack is not configured. Add a Slack token in Settings.',
      code: 'SLACK_NOT_CONFIGURED',
    };
  }
  return {
    status: 'ok',
    teamName: 'Zunelo Mock Workspace',
    userName: 'currentUser',
    userId: 'U_CURRENT',
    checkedAt,
    error: null,
    code: null,
  };
}

export class MockSlackClient implements ISlackClient {
  private threads: SlackThread[];
  private config: ClientConfig;

  constructor(config: ClientConfig = DEFAULT_CONFIG) {
    this.config = config;

    const slackItems = ITEMS.filter((i) => i.source === 'slack');
    this.threads = slackItems.map((item, idx) => {
      // Clone messages so mutating one thread's messages never bleeds back
      // into the shared fixture array.
      const messages: SlackMessage[] = (item.messages || []).map((m) => ({ ...m }));

      const participants = Array.from(new Set(messages.map((m) => m.user.id))).map((id) => {
        return messages.find((m) => m.user.id === id)!.user;
      });

      const threadTs = `1723630680.000${100 + idx * 10}`;
      const channelId = `C${item.primary.replace('#', '')}`;
      return {
        threadTs,
        channel: {
          id: channelId,
          name: item.primary,
          topic: item.threadMeta || 'Discusiones de ingeniería',
        },
        messages,
        participants,
        unread: true,
        permalink: `https://zunelo-mock.slack.com/archives/${channelId}/p${threadTs.replace('.', '')}`,
        mentionsCurrentUser:
          !!item.tags?.includes('Menciones') || !!item.tags?.includes('Te nombraron'),
        implicitlyAddressesUser: !!item.tags?.includes('Te nombraron'),
        ...(item.tags?.includes('Tu ámbito')
          ? { competenceReasons: [`channel:${item.primary.replace(/^#/, '')}`, 'keyword:carrier'] }
          : {}),
        linkedTicketKey: item.ticket,
        tags: item.tags,
        aiSummary: {
          summary: item.ai || 'Discusión activa en el canal.',
          actionItem: item.next || 'Responder al hilo',
        },
        updatedAt: '2026-08-14T09:12:00.000Z',
        messagesHydrated: true,
        zuneloMeta: {
          status: item.st,
          ai: item.ai,
          next: item.next,
          tags: item.tags,
          refs: item.refs,
        },
      };
    });
  }

  async getThreads(): Promise<ApiEnvelope<SlackThread[]>> {
    if (isMockSourceDisconnected('slack') || isMockSourceRejected('slack')) {
      return simulateNetwork(() => [], this.config);
    }
    return simulateNetwork(() => [...this.threads], this.config);
  }

  async getConversations(_options?: { fresh?: boolean }): Promise<ApiEnvelope<never[]>> {
    return simulateNetwork(() => [], this.config);
  }

  async getThread(threadTs: string): Promise<ApiEnvelope<SlackThread | null>> {
    return simulateNetwork(() => {
      const match = this.threads.find(
        (t) => t.threadTs === threadTs || t.channel.name === threadTs,
      );
      return match ? { ...match } : null;
    }, this.config);
  }

  async postReply(
    channelName: string,
    threadTs: string,
    text: string,
    _idempotencyKey?: string,
  ): Promise<ApiEnvelope<SlackThread>> {
    return simulateNetwork(() => {
      const thread = this.threads.find(
        (t) =>
          t.channel.name === channelName ||
          t.threadTs === threadTs ||
          `SL-${t.channel.name.replace('#', '')}` === threadTs,
      );
      if (!thread) {
        throw new Error(`Slack thread not found for channel ${channelName} and ts ${threadTs}`);
      }

      const now = new Date();
      const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

      const newMsg: SlackMessage = {
        ts: `${Math.floor(Date.now() / 1000)}.000${thread.messages.length + 1}`,
        user: {
          id: 'U_CURRENT',
          name: 'currentUser',
          realName: 'Tú',
          initials: 'TU',
          color: 'var(--z-accent)',
        },
        text,
        timeFormatted: timeStr,
        isoTime: now.toISOString(),
        reactions: [],
      };

      thread.messages.push(newMsg);
      if (!thread.participants.some((participant) => participant.id === newMsg.user.id)) {
        thread.participants = [...thread.participants, newMsg.user];
      }
      thread.updatedAt = now.toISOString();
      return { ...thread };
    }, this.config);
  }

  async generateAiSummary(
    channelName: string,
    threadTs: string,
    options?: { force?: boolean; mode?: 'auto' | 'incremental' | 'full' },
  ): Promise<
    ApiEnvelope<{ summary: string; actionItem: string; refreshed?: boolean; mode?: string }>
  > {
    return simulateNetwork(() => {
      const thread = this.threads.find(
        (t) =>
          t.channel.name === channelName ||
          t.threadTs === threadTs ||
          `SL-${t.channel.name.replace('#', '')}` === threadTs,
      );
      if (!thread) {
        throw new Error(`Slack thread not found`);
      }
      const force = options?.force === true;
      return {
        summary:
          thread.aiSummary?.summary ||
          'Resumen generado por IA a partir de los mensajes del canal.',
        actionItem: thread.aiSummary?.actionItem || 'Responder al hilo con tu decisión técnica.',
        refreshed: force,
        mode: options?.mode ?? (force ? 'full' : 'cached'),
      };
    }, this.config);
  }

  async generateReplyDraft(
    channelName: string,
    threadTs: string,
    hint?: string,
  ): Promise<ApiEnvelope<{ draft: string; provider?: string; model?: string }>> {
    return simulateNetwork(() => {
      const thread = this.threads.find(
        (t) =>
          t.channel.name === channelName ||
          t.threadTs === threadTs ||
          t.attentionId === threadTs ||
          `SL-${t.channel.name.replace('#', '')}` === threadTs,
      );
      if (!thread) {
        throw new Error(`Slack thread not found`);
      }
      const action = thread.aiSummary?.actionItem?.trim();
      const latest = thread.messages[thread.messages.length - 1]?.text?.trim();
      const base = action
        ? `Gracias por el contexto. ${action.replace(/\.$/, '')}. Confirmo por aquí cuando avance.`
        : latest
          ? `Gracias — lo reviso (“${latest.length > 80 ? `${latest.slice(0, 77)}…` : latest}”) y te confirmo.`
          : 'Gracias por el contexto. Lo reviso con el equipo y confirmo por aquí.';
      const draft = hint?.trim() ? `${base} ${hint.trim()}` : base;
      return { draft, provider: 'mock' };
    }, this.config);
  }

  async askAboutThread(
    channelName: string,
    threadTs: string,
    question: string,
  ): Promise<ApiEnvelope<{ answer: string; provider?: string; model?: string }>> {
    return simulateNetwork(() => {
      const thread = this.threads.find(
        (t) =>
          t.channel.name === channelName ||
          t.threadTs === threadTs ||
          t.attentionId === threadTs ||
          `SL-${t.channel.name.replace('#', '')}` === threadTs,
      );
      if (!thread) {
        throw new Error(`Slack thread not found`);
      }
      const q = question.trim() || '¿de qué trata el hilo?';
      const summary = thread.aiSummary?.summary?.trim();
      const answer = summary
        ? `Sobre “${q}”: ${summary}`
        : `Sobre “${q}”: el hilo en ${thread.channel.name} aún no tiene un resumen persistido.`;
      return { answer, provider: 'mock' };
    }, this.config);
  }

  async getConnection(): Promise<ApiEnvelope<SlackConnectionAck>> {
    return simulateNetwork(() => buildMockSlackConnectionAck(), this.config);
  }

  async testConnection() {
    return simulateNetwork(
      () => ({
        provider: 'slack',
        status: isMockSourceRejected('slack')
          ? ('error' as const)
          : isMockSourceDisconnected('slack')
            ? ('not_configured' as const)
            : ('ok' as const),
        message: isMockSourceRejected('slack')
          ? mockRejectedCredentialMessage('slack')
          : isMockSourceDisconnected('slack')
            ? 'Conecta Slack para verificar la conexión.'
            : 'Conexión Slack simulada correctamente.',
        checkedAt: new Date().toISOString(),
      }),
      this.config,
    );
  }
  async refresh(hours = 24) {
    return simulateNetwork(
      () => ({ refreshed: true, hours, count: this.threads.length }),
      this.config,
    );
  }
  async disconnectOAuth() {
    return {
      data: {} as import('@zunelo/shared').UserSettingsPublic,
      serverTime: new Date().toISOString(),
    };
  }
}
